import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import MovieCard from "@/components/feature/MovieCard";
import ScoreBadge from "@/components/base/ScoreBadge";
import { useSeo } from "@/hooks/useSeo";
import { useFavorites } from "@/hooks/useFavorites";
import {
  formatDate,
  formatRuntime,
  getGenreLabel,
  getMovieById,
  getRelatedMovies,
} from "@/lib/movie";
import RatingBreakdown from "./components/RatingBreakdown";
import AudienceReviews from "./components/AudienceReviews";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState<string | null>(null);

  useSeo({
    title: movie ? `${movie.title}详情介绍 - 最新电影` : "影片详情 - 最新电影",
    description: movie
      ? `${movie.title}（${movie.enTitle}）：${movie.synopsis} 综合评分 ${movie.rating.toFixed(1)} 分，${movie.year} 年 ${formatDate(movie.releaseDate)} 上映。`
      : "查看电影详情介绍、评分与相关推荐，尽在最新电影。",
    keywords: movie
      ? `${movie.title},${movie.title}评分,${movie.title}上映时间,${movie.title}剧情介绍,最新电影`
      : "电影详情,电影评分,电影介绍",
    canonicalPath: `/detail/${id ?? ""}`,
  });

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!movie) {
    return (
      <PageLayout>
        <div className="w-full max-w-[1360px] mx-auto px-4 md:px-6 py-24 text-center">
          <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-3xl"></i>
          </span>
          <h1 className="mt-5 text-xl font-heading font-bold text-foreground-950">电影信息暂不可用</h1>
          <p className="mt-2 text-sm text-foreground-500">该影片可能已下架或链接有误</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </PageLayout>
    );
  }

  const related = getRelatedMovies(movie, 4);
  const fav = isFavorite(movie.id);

  const reviews = [
    {
      user: "影迷·小舟",
      score: Math.min(10, movie.rating + 0.3),
      text: `《${movie.title}》的节奏把握得很好，中段几乎没有冷场。${movie.genres.map((g) => getGenreLabel(g)).join("、")}元素融合自然，值得走进影院观看。`,
    },
    {
      user: "资深观影人",
      score: Math.max(6, movie.rating - 0.4),
      text: `画面和配乐是这部片子的加分项，${movie.director}的调度很有个人风格。故事稍显克制，但结尾处理得很动人。`,
    },
    {
      user: "周末观影团",
      score: movie.rating,
      text: `整体完成度不错，${movie.duration} 分钟的片长信息量很足。推荐给喜欢${getGenreLabel(movie.category)}题材的观众。`,
    },
  ];

  const handleFavorite = () => {
    const nowFav = toggleFavorite(movie.id);
    setToast(nowFav ? "已加入我的收藏" : "已取消收藏");
  };

  return (
    <PageLayout>
      {/* Banner */}
      <div className="relative w-full h-[280px] md:h-[360px] overflow-hidden bg-primary-950">
        <img
          src={movie.backdrop}
          alt={`${movie.title} 电影剧照`}
          title={`${movie.title} - 最新电影详情`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-primary-950/60 to-primary-950/40"></div>
        <div className="absolute top-0 left-0 w-full">
          <div className="w-full max-w-[1360px] mx-auto px-4 md:px-6 pt-6">
            <nav className="flex items-center gap-2 text-xs text-background-200">
              <Link to="/" className="hover:text-background-50 transition-colors cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link
                to={`/category/${movie.category}`}
                className="hover:text-background-50 transition-colors cursor-pointer"
              >
                {getGenreLabel(movie.category)}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-background-50">{movie.title}</span>
            </nav>
          </div>
        </div>
      </div>

      <div className="w-full max-w-[1360px] mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 lg:gap-10 -mt-24 md:-mt-28 relative z-10">
          {/* Poster column */}
          <div>
            <div className="w-[190px] md:w-[300px] rounded-lg overflow-hidden border-4 border-background-50 bg-background-200">
              <img
                src={movie.poster}
                alt={`${movie.title} 电影主海报`}
                title={`${movie.title} - 最新电影`}
                className="w-full aspect-[2/3] object-cover object-top"
              />
            </div>
            <div className="hidden lg:flex flex-col gap-3 mt-5">
              <button
                type="button"
                onClick={handleFavorite}
                className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  fav
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                <i className={`${fav ? "ri-heart-3-fill" : "ri-heart-3-line"} text-lg`}></i>
                {fav ? "已收藏" : "收藏这部影片"}
              </button>
              <Link
                to={`/category/${movie.category}`}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回分类页
              </Link>
            </div>
          </div>

          {/* Info column */}
          <div className="pt-4 lg:pt-0">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {movie.genres.map((g) => (
                <Link
                  key={g}
                  to={`/category/${g}`}
                  className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors cursor-pointer"
                >
                  {getGenreLabel(g)}
                </Link>
              ))}
              <span className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-600">
                {movie.region}
              </span>
            </div>
            <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
              <h1 className="text-3xl md:text-4xl font-heading font-black text-foreground-950">
                {movie.title}
              </h1>
              <span className="text-base text-foreground-500 mb-1">{movie.enTitle}</span>
            </div>
            <p className="mt-2 text-sm text-accent-600 italic">“{movie.tagline}”</p>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-5">
              <ScoreBadge value={movie.rating} size="lg" />
              <span className="flex items-center gap-1.5 text-sm text-foreground-600">
                <i className="ri-calendar-line text-foreground-400"></i>
                {formatDate(movie.releaseDate)}
              </span>
              <span className="flex items-center gap-1.5 text-sm text-foreground-600">
                <i className="ri-time-line text-foreground-400"></i>
                {formatRuntime(movie.duration)}
              </span>
              <span className="flex items-center gap-1.5 text-sm text-foreground-600">
                <i className="ri-movie-line text-foreground-400"></i>
                {movie.year} 年上映
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-7 pt-6 border-t border-background-200">
              <div className="flex gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-50 text-primary-600 shrink-0">
                  <i className="ri-user-star-line text-lg"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-400">导演</p>
                  <p className="text-sm font-medium text-foreground-900">{movie.director}</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-50 text-primary-600 shrink-0">
                  <i className="ri-group-line text-lg"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-400">主演</p>
                  <p className="text-sm font-medium text-foreground-900">{movie.cast.join(" / ")}</p>
                </div>
              </div>
            </div>

            <div className="mt-7">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-3">影片简介</h2>
              <p className="text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                {movie.synopsis}
              </p>
            </div>
          </div>
        </div>

        {/* Story + ratings */}
        <div className="mt-12 md:mt-14">
          <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-7">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
              <h2 className="text-lg font-heading font-bold text-foreground-950">剧情介绍</h2>
            </div>
            <p className="text-sm md:text-[15px] text-foreground-700 leading-loose">{movie.story}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6 mt-6">
            <RatingBreakdown data={movie.ratingBreakdown} />
            <AudienceReviews reviews={reviews} />
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-12 md:mt-14 pb-8">
            <div className="flex items-center gap-2 mb-6">
              <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">
                相关推荐
              </h2>
              <span className="text-xs text-foreground-500 ml-1">同类题材或同导演作品</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {related.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-10 pt-6 border-t border-background-200">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-accent-600 transition-colors cursor-pointer"
              >
                <i className="ri-home-4-line"></i>
                返回首页
              </Link>
              <Link
                to={`/category/${movie.category}`}
                className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-accent-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                更多{getGenreLabel(movie.category)}电影
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Mobile fixed favorite bar */}
      <div className="lg:hidden fixed bottom-0 left-0 w-full z-40 bg-background-50 border-t border-background-200 px-4 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground-950 truncate">{movie.title}</p>
          <p className="text-xs text-foreground-500">综合评分 {movie.rating.toFixed(1)} 分</p>
        </div>
        <button
          type="button"
          onClick={handleFavorite}
          className={`inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
            fav ? "bg-accent-500 text-background-50" : "bg-primary-500 text-background-50"
          }`}
        >
          <i className={`${fav ? "ri-heart-3-fill" : "ri-heart-3-line"} text-lg`}></i>
          {fav ? "已收藏" : "收藏"}
        </button>
      </div>
      <div className="lg:hidden h-20"></div>

      {toast && (
        <div className="fixed bottom-24 lg:bottom-8 left-1/2 -translate-x-1/2 z-[60] animate-fade-in-up">
          <div className="flex items-center gap-2 px-5 py-3 rounded-md bg-primary-950 text-background-50 text-sm">
            <i className="ri-checkbox-circle-fill text-secondary-400 text-lg"></i>
            {toast}
          </div>
        </div>
      )}
    </PageLayout>
  );
}